import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, User, FileText, Ship, ArrowRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl, isValidImageUrl } from '../utils/imageUrl';

const NewsList = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
    } catch (error) {
      console.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format relative date
  const getRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Helper to get full date label for grouping
  const getDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const articleDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - articleDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Sample news for demonstration
  const sampleNews = [
    {
      id: '1',
      title: 'Caribbean Shipping Outlook 2025: Record Growth Expected',
      excerpt: 'Industry experts predict significant growth in Caribbean maritime trade, driven by increased tourism and regional trade agreements.',
      featured_image: 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Industry', 'Trade'],
      author_name: 'CTC Editorial',
      published_at: new Date().toISOString()
    },
    {
      id: '2',
      title: 'New Air Cargo Routes Connect Caribbean Markets',
      excerpt: 'Major airlines announce expanded cargo services connecting Miami, San Juan, and key Caribbean destinations.',
      featured_image: 'https://images.pexels.com/photos/358319/pexels-photo-358319.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Air Freight', 'Routes'],
      author_name: 'CTC Editorial',
      published_at: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: '3',
      title: 'Customs Modernization Initiative Launched',
      excerpt: 'Regional customs authorities announce digital transformation to streamline cross-border freight movements.',
      featured_image: 'https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Customs', 'Technology'],
      author_name: 'CTC Editorial',
      published_at: new Date(Date.now() - 172800000).toISOString()
    }
  ];

  const displayNews = news.length > 0 ? news : sampleNews;

  // Group remaining articles by date
  const groupedNews = useMemo(() => {
    const remaining = displayNews.slice(1);
    const groups = {};
    
    remaining.forEach(article => {
      const date = new Date(article.published_at);
      const dateKey = date.toDateString(); // e.g., "Sat Feb 08 2026"
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          label: getDateLabel(article.published_at),
          fullDate: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          articles: []
        };
      }
      groups[dateKey].articles.push(article);
    });
    
    // Sort by date (most recent first)
    return Object.values(groups).sort((a, b) => 
      new Date(b.articles[0].published_at) - new Date(a.articles[0].published_at)
    );
  }, [displayNews]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 px-4 py-6 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-outfit font-bold text-ocean-900">News & Updates</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-2">Stay informed with the latest Caribbean freight industry news</p>
          </div>

          {/* Featured Article - Latest News */}
          {displayNews.length > 0 && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-red-500 text-white animate-pulse">LATEST</Badge>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {getRelativeDate(displayNews[0].published_at)}
                </span>
              </div>
              <Link to={`/news/${displayNews[0].id}`} className="block">
                <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-cyan-500">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/2">
                      <img
                        src={isValidImageUrl(displayNews[0].featured_image) ? toFullUrl(displayNews[0].featured_image) : 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800'}
                        alt={displayNews[0].title}
                        className="w-full h-48 sm:h-64 md:h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800';
                        }}
                      />
                    </div>
                    <div className="md:w-1/2 p-4 sm:p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                        {displayNews[0].tags?.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs text-cyan-600 border-cyan-300">{tag}</Badge>
                        ))}
                      </div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-outfit font-bold text-ocean-900 mb-2 sm:mb-3 line-clamp-3">
                        {displayNews[0].title}
                      </h2>
                      <p className="text-sm sm:text-base text-slate-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3">{displayNews[0].excerpt}</p>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>{displayNews[0].author_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>{new Date(displayNews[0].published_at).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}</span>
                        </div>
                      </div>
                      <Button className="w-fit bg-cyan-500 hover:bg-cyan-600 text-sm sm:text-base">
                        Read Full Article <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          )}

          {/* Articles Grouped by Date */}
          {groupedNews.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-8">
              {/* Date Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full">
                  <Calendar className="h-4 w-4 text-cyan-600" />
                  <span className="text-sm font-medium text-slate-700">{group.label}</span>
                </div>
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-xs text-slate-400 hidden sm:block">{group.fullDate}</span>
              </div>
              
              {/* Articles Grid for this Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {group.articles.map((article, index) => (
                  <Link key={article.id} to={`/news/${article.id}`} className="block">
                    <Card className="hover:shadow-lg transition-shadow overflow-hidden group h-full">
                      <div className="relative">
                        <img
                          src={isValidImageUrl(article.featured_image) ? toFullUrl(article.featured_image) : `https://images.pexels.com/photos/${index % 2 === 0 ? '358319' : '1427107'}/pexels-photo-${index % 2 === 0 ? '358319' : '1427107'}.jpeg?auto=compress&cs=tinysrgb&w=800`}
                          alt={article.title}
                          className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://images.pexels.com/photos/${index % 2 === 0 ? '358319' : '1427107'}/pexels-photo-${index % 2 === 0 ? '358319' : '1427107'}.jpeg?auto=compress&cs=tinysrgb&w=800`;
                          }}
                        />
                        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex gap-1 flex-wrap">
                          {article.tags?.slice(0, 2).map(tag => (
                            <Badge key={tag} className="bg-white/90 text-slate-700 text-[10px] sm:text-xs">{tag}</Badge>
                          ))}
                        </div>
                        {/* Time badge */}
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {getRelativeDate(article.published_at)}
                        </div>
                      </div>
                      <CardHeader className="p-3 sm:p-4 pb-2">
                        <CardTitle className="text-base sm:text-lg line-clamp-2 group-hover:text-cyan-600 transition-colors">
                          {article.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 pt-0">
                        <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4 line-clamp-2">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-[10px] sm:text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="truncate max-w-[80px] sm:max-w-none">{article.author_name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(article.published_at).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit'
                            })}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {news.length === 0 && (
            <div className="text-center mt-8 text-slate-500">
              <p className="text-sm">These are sample articles. Real news content will appear as it's published.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsList;
